import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useFocusRescue } from '../details/use-focus-rescue';
import { useStatusMessage } from '../details/use-status-message';
import { adminErrorMessage } from './error-message';
import { NewUserForm } from './NewUserForm';
import { SectionHeading } from './SectionHeading';
import { sortUsers } from './sort-users';
import { UserCard } from './UserCard';
import { useUsers } from './use-users';
import section from './Section.module.css';
import styles from './UsersScreen.module.css';

/** Lista de usuários, com criação e edição no próprio lugar. Sem exclusão: a API não a oferece. */
export function UsersScreen({ viewerId }: { viewerId: string }): ReactElement {
  const headingId = useId();
  const users = useUsers();
  const status = useStatusMessage();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [focusUserId, setFocusUserId] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const newUserRef = useRef<HTMLButtonElement>(null);
  const returnFocusToNew = useRef(false);

  // "Tentar de novo" some quando a lista chega e levaria o foco ao <body>.
  const rescue = useFocusRescue(!users.isError, headingRef);
  const retry = (): void => {
    rescue.arm();
    void users.refetch().then((result) => {
      if (result.isError) rescue.disarm();
    });
  };

  // Cancelar o formulário devolve o foco ao botão que o abriu (que reaparece neste render).
  useEffect(() => {
    if (creating || !returnFocusToNew.current) return;
    returnFocusToNew.current = false;
    newUserRef.current?.focus();
  }, [creating]);

  const sorted = useMemo(() => sortUsers(users.data ?? []), [users.data]);
  const handleFocused = useCallback(() => setFocusUserId(null), []);
  const loadFailed = users.isError && users.data === undefined;

  return (
    <section aria-labelledby={headingId} className={section.screen}>
      <SectionHeading id={headingId} ref={headingRef}>
        Usuários
      </SectionHeading>
      <p className={section.lead}>
        Quem pode entrar na caixa de entrada. Usuários não são excluídos: desative quem não deve
        mais acessar (o histórico de mensagens e notas fica preservado).
      </p>

      {users.data !== undefined && !creating && (
        <div className={styles.toolbar}>
          <Button
            ref={newUserRef}
            variant="primary"
            onClick={() => {
              status.announce('');
              setEditingId(null);
              setCreating(true);
            }}
          >
            Novo usuário
          </Button>
        </div>
      )}

      {creating && (
        <NewUserForm
          onCancel={() => {
            returnFocusToNew.current = true;
            setCreating(false);
          }}
          onCreated={(user) => {
            setCreating(false);
            setFocusUserId(user.id);
            status.announce(`Usuário criado: ${user.name}.`);
          }}
        />
      )}

      {/* A região existe antes do texto: leitor de tela só anuncia o que entra numa região já presente. */}
      <StatusRegion message={status.message} />

      {users.isPending && (
        <p role="status" className={section.loading}>
          Carregando usuários…
        </p>
      )}

      {loadFailed && (
        <div className={section.loadError}>
          <p role="alert" className={section.loadErrorText}>
            Não foi possível carregar os usuários.{' '}
            {adminErrorMessage(users.error, 'Tente de novo.')}
          </p>
          <Button busy={users.isFetching} onClick={retry}>
            Tentar de novo
          </Button>
        </div>
      )}

      {users.isError && users.data !== undefined && (
        <div className={styles.refreshError}>
          <p role="alert" className={styles.refreshErrorText}>
            Não foi possível atualizar a lista; o que aparece abaixo pode estar desatualizado.
          </p>
          <Button busy={users.isFetching} onClick={retry}>
            Tentar de novo
          </Button>
        </div>
      )}

      {users.data !== undefined && sorted.length === 0 && (
        <p className={styles.empty}>Nenhum usuário cadastrado ainda.</p>
      )}

      {sorted.length > 0 && (
        <ul aria-label="Usuários" className={styles.list}>
          {sorted.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              isYou={user.id === viewerId}
              viewerId={viewerId}
              editing={editingId === user.id}
              onToggleEdit={() => {
                status.announce('');
                setEditingId((current) => (current === user.id ? null : user.id));
              }}
              focusRequested={focusUserId === user.id}
              onFocusHandled={handleFocused}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
